import type { TFunction } from 'i18next';

import type { ProductItem } from '@/lib/api/products';
import type { UpdateProductInput } from '@/lib/api/products';
import type { UpdateVariantInput } from '@/lib/api/variants';
import type { CurrencyOptionsType } from '@/components/form/currency-price-input';
import type { LockedBy } from '@/lib/currency/currencyOptionsKeys';
import { convertMinorUnits, minorUnitsToMajorUnits, moneyStringOrNumberToMinorUnits } from '@/lib/money';

import type {
  BulkEditColumnId,
  BulkEditProductEdits,
  BulkEditRow,
  BulkEditRowEdits,
  BulkEditVariantEdits,
  BulkEditVariantSource,
} from './bulk-edit.types';
import {
  ALL_BULK_EDIT_STATIC_COLUMN_IDS,
  DEFAULT_VISIBLE_STATIC_COLUMNS,
} from './bulk-edit.types';
import type { BulkEditStoreCurrencies } from './bulk-edit-currencies';

type RawVariant = NonNullable<ProductItem['variants']>[number];

type ExtendedProduct = ProductItem & {
  unitLabel?: string | null;
  accountingCode?: string | null;
  taxCategory?: string | null;
};

const VARIANT_ONLY_EDIT_KEYS: (keyof BulkEditVariantEdits)[] = [
  'variantDescription',
  'currencyEdits',
  'sku',
  'grams',
  'inStock',
  'creditPrice',
  'imageUrl',
  'isDefault',
  'recurring',
  'interval',
  'intervalCount',
];

const PRODUCT_ONLY_EDIT_KEYS: (keyof BulkEditProductEdits)[] = [
  'productDescription',
  'unitLabel',
  'accountingCode',
  'mappedTaxCategoryId',
  'isShippable',
  'isTaxable',
  'autoSync',
];

export function readBulkEditBoolean(value: unknown, fallback: boolean): boolean {
  if (value === true || value === false) return value;
  return fallback;
}

function getRawVariantFromProduct(product: ProductItem, variantId?: string) {
  if (!variantId) return null;
  return product.variants?.find((variant) => variant.id === variantId) ?? null;
}

type RawVariantScalars = {
  active?: boolean;
  inStock?: boolean;
};

function readRawVariantBoolean(
  rawVariant: NonNullable<ReturnType<typeof getRawVariantFromProduct>>,
  field: keyof RawVariantScalars,
  fallback: boolean,
): boolean {
  const value = (rawVariant as RawVariantScalars)[field];
  return readBulkEditBoolean(value, fallback);
}

function resolveRowActive(
  product: ProductItem,
  variantId: string | undefined,
  edits: BulkEditRowEdits,
  options: { allowProductFallback?: boolean } = {},
): boolean {
  if (edits.active !== undefined) return edits.active;

  const rawVariant = getRawVariantFromProduct(product, variantId);
  if (rawVariant) return readRawVariantBoolean(rawVariant, 'active', true);

  if (options.allowProductFallback && typeof product.active === 'boolean') {
    return product.active;
  }

  return true;
}

function resolveRowInStock(
  product: ProductItem,
  variantId: string | undefined,
  edits: BulkEditRowEdits,
): boolean {
  if (edits.inStock !== undefined) return edits.inStock;

  const rawVariant = getRawVariantFromProduct(product, variantId);
  if (rawVariant) return readRawVariantBoolean(rawVariant, 'inStock', true);

  return true;
}

function normalizeVariant(raw: RawVariant): BulkEditVariantSource {
  const extended = raw as RawVariant & {
    productId?: string;
    grams?: number | null;
    active?: boolean;
    inStock?: boolean;
    compareAtPrice?: number | null;
    creditPrice?: number | null;
  };

  return {
    id: raw.id,
    productId: extended.productId ?? '',
    default: Boolean(extended.default),
    name: raw.name,
    description: raw.description,
    sku: raw.sku,
    grams: extended.grams ?? null,
    imageUrl: raw.imageUrl,
    active: readBulkEditBoolean(extended.active, true),
    inStock: readBulkEditBoolean(extended.inStock, true),
    creditPrice: extended.creditPrice ?? null,
    prices: (raw.prices ?? []).map((price) => ({
      id: price.id,
      default: Boolean(price.default),
      currencyOptions: price.currencyOptions ?? {},
      recurring: price.recurring,
      interval: price.interval ?? null,
      intervalCount: price.intervalCount ?? null,
      rebillWithOrder: price.rebillWithOrder,
      rebillMode: price.rebillMode,
      addDeliveryOnRebill: price.addDeliveryOnRebill ?? false,
      isExternalSellingPlan: price.isExternalSellingPlan ?? false,
    })) as BulkEditVariantSource['prices'],
  };
}

function getDefaultPrice(variant: BulkEditVariantSource) {
  return variant.prices.find((p) => p.default) ?? variant.prices[0] ?? null;
}

function getDefaultVariant(product: ProductItem): BulkEditVariantSource | null {
  const variants = product.variants ?? [];
  if (variants.length === 0) return null;
  const match = variants.find((v) => v.default) ?? variants[0];
  return normalizeVariant(match);
}

function toCurrencyOptions(
  options: Record<string, { amount: number; date?: string; rate?: number; lock?: boolean; lockedBy?: LockedBy }>,
): CurrencyOptionsType {
  return Object.fromEntries(
    Object.entries(options).map(([currency, option]) => [
      currency,
      {
        amount: option.amount,
        date: option.date ?? new Date().toISOString(),
        rate: option.rate ?? 1,
        lock: option.lock ?? false,
        ...(option.lockedBy && { lockedBy: option.lockedBy }),
      },
    ]),
  );
}

function pickProductEdits(rowEdits: BulkEditRowEdits): BulkEditProductEdits {
  const result: BulkEditProductEdits = {};
  for (const key of PRODUCT_ONLY_EDIT_KEYS) {
    if (key in rowEdits && rowEdits[key] !== undefined) {
      (result as Record<string, unknown>)[key] = rowEdits[key];
    }
  }
  if (rowEdits.name !== undefined) result.name = rowEdits.name;
  if (rowEdits.active !== undefined) result.active = rowEdits.active;
  return result;
}

function pickVariantEdits(rowEdits: BulkEditRowEdits): BulkEditVariantEdits {
  const result: BulkEditVariantEdits = {};
  for (const key of VARIANT_ONLY_EDIT_KEYS) {
    if (key in rowEdits && rowEdits[key] !== undefined) {
      (result as Record<string, unknown>)[key] = rowEdits[key];
    }
  }
  if (rowEdits.name !== undefined) result.name = rowEdits.name;
  if (rowEdits.active !== undefined) result.active = rowEdits.active;
  return result;
}

function resolveVariantFields(
  variant: BulkEditVariantSource | null | undefined,
  variantEdits: BulkEditVariantEdits,
  storeCurrencies: BulkEditStoreCurrencies,
) {
  const defaultPrice = variant ? getDefaultPrice(variant) : null;
  const currencyOptions = defaultPrice ? toCurrencyOptions(defaultPrice.currencyOptions) : {};
  const chargeCurrency = storeCurrencies.chargeCurrencies[0] ?? storeCurrencies.baseCurrency;
  const baseAmount = currencyOptions[chargeCurrency]?.amount ?? null;

  return {
    sku: variantEdits.sku ?? variant?.sku ?? '',
    grams: variantEdits.grams ?? variant?.grams ?? null,
    inStock:
      variantEdits.inStock !== undefined
        ? variantEdits.inStock
        : readBulkEditBoolean(variant?.inStock, true),
    priceAmount: getCurrencyAmountFromEdits(currencyOptions, variantEdits, chargeCurrency) ?? baseAmount,
    creditPrice: variantEdits.creditPrice ?? variant?.creditPrice ?? null,
    variantDescription: variantEdits.variantDescription ?? variant?.description ?? '',
    imageUrl: variantEdits.imageUrl ?? variant?.imageUrl ?? null,
    isDefault: variantEdits.isDefault ?? variant?.default ?? false,
    recurring: variantEdits.recurring ?? defaultPrice?.recurring ?? false,
    interval: variantEdits.interval ?? defaultPrice?.interval ?? null,
    intervalCount: variantEdits.intervalCount ?? defaultPrice?.intervalCount ?? null,
    priceCurrency: chargeCurrency,
    defaultPriceId: defaultPrice?.id ?? null,
    currencyOptions,
  };
}

export function getCurrencyAmountFromEdits(
  currencyOptions: CurrencyOptionsType,
  variantEdits: BulkEditVariantEdits,
  currency: string,
): number | null {
  const upper = currency.toUpperCase();
  const edited = variantEdits.currencyEdits?.[upper];
  if (edited != null) return edited;
  return currencyOptions[upper]?.amount ?? null;
}

export function getRowCurrencyAmount(
  row: BulkEditRow,
  currency: string,
  edits: Map<string, BulkEditRowEdits>,
): number | null {
  const rowEdits = edits.get(row.key);
  const upper = currency.toUpperCase();
  const edited = rowEdits?.currencyEdits?.[upper];
  if (edited != null) return edited;
  return row.currencyOptions[upper]?.amount ?? null;
}

/**
 * Whether the price this row writes to is a Shopify selling plan mirrored into Tagada.
 *
 * Selects the price the same way the save path does (`buildVariantSavePayload`): protecting
 * a different price from the one the grid actually writes would leave the hole open. Editing such
 * an amount is silent money loss, because the plan's own discount is applied on top of it at
 * checkout (TAG-2536).
 */
export function isRowPriceExternallyManaged(row: BulkEditRow): boolean {
  const prices = row.sourceVariant?.prices ?? [];
  const targetPrice = prices.find((price) => price.default) ?? prices[0];
  return targetPrice?.isExternalSellingPlan === true;
}

export function getRowKey(kind: 'product' | 'variant', id: string) {
  return `${kind}:${id}`;
}

export function parseRowKey(key: string): { kind: 'product' | 'variant'; id: string } {
  const [kind, ...rest] = key.split(':');
  return { kind: kind as 'product' | 'variant', id: rest.join(':') };
}

export function mergeBulkEditProductCache(
  cache: Map<string, ProductItem>,
  items: ProductItem[],
): Map<string, ProductItem> {
  if (items.length === 0) return cache;
  const next = new Map(cache);
  for (const product of items) {
    next.set(product.id, product);
  }
  return next;
}

function findProductIdForVariantId(
  variantId: string,
  cache: Map<string, ProductItem>,
): string | null {
  for (const product of cache.values()) {
    if (product.variants?.some((variant) => variant.id === variantId)) {
      return product.id;
    }
  }
  return null;
}

/** Product IDs touched by pending edits (across paginated pages). */
export function getBulkEditProductIdsFromEdits(
  edits: Map<string, BulkEditRowEdits>,
  cache: Map<string, ProductItem>,
): Set<string> {
  const productIds = new Set<string>();
  for (const key of edits.keys()) {
    const { kind, id } = parseRowKey(key);
    if (kind === 'product') {
      productIds.add(id);
      continue;
    }
    const productId = findProductIdForVariantId(id, cache);
    if (productId) productIds.add(productId);
  }
  return productIds;
}

export function getBulkEditProductsForSave(
  edits: Map<string, BulkEditRowEdits>,
  cache: Map<string, ProductItem>,
): ProductItem[] {
  return [...getBulkEditProductIdsFromEdits(edits, cache)]
    .map((id) => cache.get(id))
    .filter((product): product is ProductItem => !!product);
}

export function buildBulkEditRowsForSave(
  edits: Map<string, BulkEditRowEdits>,
  cache: Map<string, ProductItem>,
  storeCurrencies: BulkEditStoreCurrencies,
): BulkEditRow[] {
  const productsForSave = getBulkEditProductsForSave(edits, cache);
  const expandedProductIds = getBulkEditProductIdsFromEdits(edits, cache);
  return buildBulkEditRows(productsForSave, expandedProductIds, edits, storeCurrencies);
}

export function buildBulkEditRows(
  products: ProductItem[],
  expandedProductIds: Set<string>,
  edits: Map<string, BulkEditRowEdits>,
  storeCurrencies: BulkEditStoreCurrencies,
): BulkEditRow[] {
  const rows: BulkEditRow[] = [];

  for (const product of products) {
    const extendedProduct = product as ExtendedProduct;
    const variants = product.variants ?? [];
    const productKey = getRowKey('product', product.id);
    const productEdits = edits.get(productKey) ?? {};
    const defaultVariant = getDefaultVariant(product);
    const variantFields = resolveVariantFields(
      defaultVariant,
      productEdits as BulkEditVariantEdits,
      storeCurrencies,
    );

    rows.push({
      key: productKey,
      kind: 'product',
      productId: product.id,
      name: productEdits.name ?? product.name,
      imageUrl:
        variants.length <= 1
          ? (variantFields.imageUrl ?? product.image ?? null)
          : (product.image ?? null),
      hasChildVariants: variants.length > 1,
      isExpanded: expandedProductIds.has(product.id),
      variantCount: variants.length,
      active:
        variants.length <= 1
          ? resolveRowActive(product, defaultVariant?.id, productEdits, { allowProductFallback: true })
          : resolveRowActive(product, undefined, productEdits, { allowProductFallback: true }),
      isShippable: productEdits.isShippable ?? product.isShippable ?? false,
      isTaxable: productEdits.isTaxable ?? product.isTaxable ?? false,
      autoSync: productEdits.autoSync ?? product.autoSync ?? false,
      productDescription: productEdits.productDescription ?? product.description ?? '',
      variantDescription: variantFields.variantDescription,
      unitLabel: productEdits.unitLabel ?? extendedProduct.unitLabel ?? '',
      accountingCode: productEdits.accountingCode ?? extendedProduct.accountingCode ?? '',
      taxCategory: productEdits.taxCategory ?? extendedProduct.taxCategory ?? '',
      mappedTaxCategoryId:
        productEdits.mappedTaxCategoryId !== undefined
          ? productEdits.mappedTaxCategoryId
          : (product.mappedTaxCategory?.id ?? null),
      mappedTaxCategory: product.mappedTaxCategory ?? null,
      sku: variantFields.sku,
      grams: variantFields.grams,
      inStock: resolveRowInStock(product, defaultVariant?.id, productEdits),
      priceAmount: variantFields.priceAmount,
      creditPrice: variantFields.creditPrice,
      priceCurrency: variantFields.priceCurrency,
      defaultPriceId: variantFields.defaultPriceId,
      currencyOptions: variantFields.currencyOptions,
      isDefault: variantFields.isDefault,
      recurring: variantFields.recurring,
      interval: variantFields.interval,
      intervalCount: variantFields.intervalCount,
      sourceProduct: product,
      sourceVariant: defaultVariant ?? undefined,
    });

    if (variants.length > 1 && expandedProductIds.has(product.id)) {
      for (let index = 0; index < variants.length; index++) {
        rows.push(
          buildVariantRow(product, normalizeVariant(variants[index]!), edits, storeCurrencies, {
            isLastVariantInGroup: index === variants.length - 1,
          }),
        );
      }
    }
  }

  return rows;
}

function buildVariantRow(
  product: ProductItem,
  variant: BulkEditVariantSource,
  edits: Map<string, BulkEditRowEdits>,
  storeCurrencies: BulkEditStoreCurrencies,
  treeMeta?: { isLastVariantInGroup: boolean },
): BulkEditRow {
  const extendedProduct = product as ExtendedProduct;
  const variantKey = getRowKey('variant', variant.id);
  const variantEdits = edits.get(variantKey) ?? {};
  const variantFields = resolveVariantFields(variant, variantEdits as BulkEditVariantEdits, storeCurrencies);

  return {
    key: variantKey,
    kind: 'variant',
    productId: product.id,
    variantId: variant.id,
    name: variantEdits.name ?? variant.name,
    imageUrl: variantFields.imageUrl ?? product.image ?? null,
    hasChildVariants: false,
    isExpanded: false,
    variantCount: 0,
    isLastVariantInGroup: treeMeta?.isLastVariantInGroup,
    active: resolveRowActive(product, variant.id, variantEdits),
    isShippable: product.isShippable ?? false,
    isTaxable: product.isTaxable ?? false,
    autoSync: product.autoSync ?? false,
    productDescription: extendedProduct.description ?? '',
    variantDescription: variantFields.variantDescription,
    unitLabel: extendedProduct.unitLabel ?? '',
    accountingCode: extendedProduct.accountingCode ?? '',
    taxCategory: extendedProduct.taxCategory ?? '',
    mappedTaxCategoryId: product.mappedTaxCategory?.id ?? null,
    mappedTaxCategory: product.mappedTaxCategory ?? null,
    sku: variantFields.sku,
    grams: variantFields.grams,
    inStock: resolveRowInStock(product, variant.id, variantEdits),
    priceAmount: variantFields.priceAmount,
    creditPrice: variantFields.creditPrice,
    priceCurrency: variantFields.priceCurrency,
    defaultPriceId: variantFields.defaultPriceId,
    currencyOptions: variantFields.currencyOptions,
    isDefault: variantFields.isDefault,
    recurring: variantFields.recurring,
    interval: variantFields.interval,
    intervalCount: variantFields.intervalCount,
    sourceProduct: product,
    sourceVariant: variant,
  };
}

export function applyBulkEditListPatches<T extends { items: ProductItem[] }>(
  current: T | undefined,
  productUpdates: UpdateProductInput[],
  variantUpdates: UpdateVariantInput[],
): T | undefined {
  if (!current?.items?.length) return current;

  const productPatchById = new Map(productUpdates.map((update) => [update.productId, update]));
  const variantPatchById = new Map(variantUpdates.map((update) => [update.variantId, update]));

  return {
    ...current,
    items: current.items.map((product) => {
      const productPatch = productPatchById.get(product.id);
      const variants = product.variants?.map((variant) => {
        const variantPatch = variantPatchById.get(variant.id);
        if (!variantPatch) return variant;

        const { updatedData } = variantPatch;
        return {
          ...variant,
          ...(updatedData.name !== undefined ? { name: updatedData.name } : {}),
          ...(updatedData.description !== undefined ? { description: updatedData.description } : {}),
          ...(updatedData.sku !== undefined ? { sku: updatedData.sku } : {}),
          ...(updatedData.grams !== undefined ? { grams: updatedData.grams ?? 0 } : {}),
          ...(updatedData.imageUrl !== undefined ? { imageUrl: updatedData.imageUrl } : {}),
          ...(updatedData.default !== undefined ? { default: updatedData.default } : {}),
          ...(updatedData.active !== undefined ? { active: updatedData.active } : {}),
          ...(updatedData.inStock !== undefined ? { inStock: updatedData.inStock } : {}),
          ...(updatedData.creditPrice !== undefined ? { creditPrice: updatedData.creditPrice } : {}),
        };
      });

      if (!productPatch && variants === product.variants) return product;

      const productData = productPatch?.updatedData;
      return {
        ...product,
        ...(productData?.name !== undefined ? { name: productData.name } : {}),
        ...(productData?.description !== undefined ? { description: productData.description } : {}),
        ...(productData?.active !== undefined ? { active: productData.active } : {}),
        ...(productData?.isShippable !== undefined ? { isShippable: productData.isShippable } : {}),
        ...(productData?.isTaxable !== undefined ? { isTaxable: productData.isTaxable } : {}),
        ...(productData?.autoSync !== undefined ? { autoSync: productData.autoSync } : {}),
        ...(productData?.unitLabel !== undefined ? { unitLabel: productData.unitLabel } : {}),
        ...(productData?.accountingCode !== undefined
          ? { accountingCode: productData.accountingCode }
          : {}),
        ...(productData?.taxCategory !== undefined ? { taxCategory: productData.taxCategory } : {}),
        variants,
      };
    }),
  };
}

export function mergeRowEdit(
  edits: Map<string, BulkEditRowEdits>,
  key: string,
  patch: BulkEditRowEdits,
): Map<string, BulkEditRowEdits> {
  const next = new Map(edits);
  const existing = next.get(key) ?? {};
  const merged: BulkEditRowEdits = { ...existing, ...patch };
  if (patch.currencyEdits) {
    merged.currencyEdits = { ...(existing.currencyEdits ?? {}), ...patch.currencyEdits };
  }
  next.set(key, merged);
  return next;
}

export function applyPricePercentToRows(
  rows: BulkEditRow[],
  selectedKeys: Set<string>,
  percent: number,
  edits: Map<string, BulkEditRowEdits>,
  storeCurrencies: BulkEditStoreCurrencies,
): Map<string, BulkEditRowEdits> {
  let next = edits;
  for (const row of rows) {
    if (!selectedKeys.has(row.key)) continue;
    if (row.kind === 'product' && row.hasChildVariants) continue;
    // Every row is selectable, including the ones the grid renders read-only. Without this the
    // bulk percentage would write the amount the price cell refuses to edit (TAG-2536).
    if (isRowPriceExternallyManaged(row)) continue;

    const rowEdits = next.get(row.key) ?? {};
    const currencyEdits = { ...(rowEdits.currencyEdits ?? {}) };
    let changed = false;

    for (const currency of storeCurrencies.allPriceCurrencies) {
      const amount = getRowCurrencyAmount(row, currency, next);
      if (amount == null) continue;
      currencyEdits[currency] = Math.round(amount * (1 + percent / 100));
      changed = true;
    }

    if (changed) {
      next = mergeRowEdit(next, row.key, { currencyEdits });
    }
  }
  return next;
}

export function applyStatusToRows(
  rows: BulkEditRow[],
  selectedKeys: Set<string>,
  active: boolean,
  edits: Map<string, BulkEditRowEdits>,
): Map<string, BulkEditRowEdits> {
  let next = edits;
  for (const row of rows) {
    if (!selectedKeys.has(row.key)) continue;
    next = mergeRowEdit(next, row.key, { active });
  }
  return next;
}

export function isRowDirty(key: string, edits: Map<string, BulkEditRowEdits>) {
  return edits.has(key);
}

export function isCellDirty(key: string, field: string, edits: Map<string, BulkEditRowEdits>) {
  const rowEdits = edits.get(key);
  if (rowEdits == null) return false;
  if (field.startsWith('currency:')) {
    const currency = field.slice('currency:'.length).toUpperCase();
    return rowEdits.currencyEdits?.[currency] !== undefined;
  }
  return field in rowEdits;
}

export function currencyEditField(currency: string) {
  return `currency:${currency.toUpperCase()}`;
}

function getRawVariant(product: ProductItem | undefined, variantId: string) {
  return product?.variants?.find((variant) => variant.id === variantId);
}

function normalizePricesForVariantUpdate(
  rawPrices: Array<Record<string, unknown>> | undefined,
): UpdateVariantInput['updatedData']['prices'] {
  if (!rawPrices?.length) return [];

  return rawPrices.map((price) => {
    const recurring = Boolean(price.recurring);

    return {
      ...price,
      id: String(price.id),
      default: Boolean(price.default),
      recurring,
      billingTiming: (price.billingTiming as string | undefined) ?? 'usage',
      interval: recurring ? ((price.interval as string | null | undefined) ?? 'month') : null,
      intervalCount: recurring ? ((price.intervalCount as number | null | undefined) ?? 1) : null,
      rebillWithOrder: (price.rebillWithOrder as boolean | undefined) ?? false,
      rebillMode: (price.rebillMode as 'auth' | 'capture' | null | undefined) ?? null,
      rebillStepIntervalMs: (price.rebillStepIntervalMs as number | null | undefined) ?? null,
      isExternalSellingPlan: (price.isExternalSellingPlan as boolean | undefined) ?? false,
      isInternalSellingPlan: (price.isInternalSellingPlan as boolean | undefined) ?? false,
      shopifySellingPlanId: (price.shopifySellingPlanId as string | null | undefined) ?? null,
      addDeliveryOnRebill: (price.addDeliveryOnRebill as boolean | undefined) ?? false,
      currencyOptions:
        (price.currencyOptions as Record<string, { amount: number }> | undefined) ?? {},
    };
  }) as UpdateVariantInput['updatedData']['prices'];
}

function mergeVariantEditsForSingleVariantProduct(
  rowEdits: BulkEditRowEdits,
  variantCount: number,
): BulkEditVariantEdits {
  const variantEdits = pickVariantEdits(rowEdits);
  if (variantCount !== 1) return variantEdits;

  const merged: BulkEditVariantEdits = { ...variantEdits };

  if (rowEdits.active !== undefined && merged.active === undefined) {
    merged.active = rowEdits.active;
  }

  if (rowEdits.inStock !== undefined && merged.inStock === undefined) {
    merged.inStock = rowEdits.inStock;
  }

  if (rowEdits.productDescription !== undefined && merged.variantDescription === undefined) {
    merged.variantDescription = rowEdits.productDescription;
  }

  if (rowEdits.name !== undefined && merged.name === undefined) {
    merged.name = rowEdits.name;
  }

  return merged;
}

function productEditsFromSingleVariantRow(
  row: BulkEditRow,
  variantEdits: BulkEditVariantEdits,
): BulkEditProductEdits | null {
  const variantCount = row.sourceProduct?.variants?.length ?? 0;
  if (variantCount !== 1) return null;

  const productEdits: BulkEditProductEdits = {};

  if (variantEdits.active !== undefined) {
    productEdits.active = variantEdits.active;
  }

  if (variantEdits.name !== undefined) {
    productEdits.name = variantEdits.name;
  }

  if (variantEdits.variantDescription !== undefined) {
    productEdits.productDescription = variantEdits.variantDescription;
  }

  return Object.keys(productEdits).length > 0 ? productEdits : null;
}

function productEditsFromSingleVariantProductRow(
  rowEdits: BulkEditRowEdits,
  variantCount: number,
): BulkEditProductEdits | null {
  if (variantCount !== 1) return null;

  const productEdits = pickProductEdits(rowEdits);
  const variantEdits = pickVariantEdits(rowEdits);

  if (variantEdits.active !== undefined) {
    productEdits.active = variantEdits.active;
  }

  return Object.keys(productEdits).length > 0 ? productEdits : null;
}

function mergeProductEdits(
  current: BulkEditProductEdits,
  patch: BulkEditProductEdits,
): BulkEditProductEdits {
  return { ...current, ...patch };
}

function mergeVariantEdits(
  current: BulkEditVariantEdits,
  patch: BulkEditVariantEdits,
): BulkEditVariantEdits {
  return { ...current, ...patch };
}

function enqueueProductUpdate(
  productEditsById: Map<
    string,
    { product: ProductItem; edits: BulkEditProductEdits; row?: BulkEditRow }
  >,
  product: ProductItem,
  rowEdits: BulkEditProductEdits,
  row?: BulkEditRow,
) {
  const existing = productEditsById.get(product.id);
  if (existing) {
    existing.edits = mergeProductEdits(existing.edits, rowEdits);
    if (row) existing.row = row;
    return;
  }

  productEditsById.set(product.id, { product, edits: rowEdits, row });
}

function enqueueVariantUpdate(
  variantEditsById: Map<string, { row: BulkEditRow; edits: BulkEditVariantEdits }>,
  row: BulkEditRow,
  rowEdits: BulkEditVariantEdits,
) {
  if (!row.sourceVariant) return;
  const existing = variantEditsById.get(row.sourceVariant.id);
  if (existing) {
    existing.edits = mergeVariantEdits(existing.edits, rowEdits);
    existing.row = row;
    return;
  }

  variantEditsById.set(row.sourceVariant.id, { row, edits: rowEdits });
}

export function buildProductSavePayload(
  product: ProductItem,
  rowEdits: BulkEditProductEdits,
  row?: BulkEditRow,
): UpdateProductInput | null {
  if (Object.keys(rowEdits).length === 0) return null;

  const extendedProduct = product as ExtendedProduct;

  return {
    productId: product.id,
    updatedData: {
      name: rowEdits.name ?? row?.name ?? product.name,
      description: rowEdits.productDescription ?? row?.productDescription ?? product.description ?? '',
      unitLabel: rowEdits.unitLabel ?? row?.unitLabel ?? extendedProduct.unitLabel ?? null,
      accountingCode:
        rowEdits.accountingCode ?? row?.accountingCode ?? extendedProduct.accountingCode ?? null,
      taxCategory: rowEdits.taxCategory ?? row?.taxCategory ?? extendedProduct.taxCategory ?? null,
      active: rowEdits.active ?? row?.active ?? product.active ?? true,
      isShippable: rowEdits.isShippable ?? row?.isShippable ?? product.isShippable ?? false,
      isTaxable: rowEdits.isTaxable ?? row?.isTaxable ?? product.isTaxable ?? false,
      autoSync: rowEdits.autoSync ?? row?.autoSync ?? product.autoSync ?? false,
      mappedTaxCategoryId:
        rowEdits.mappedTaxCategoryId !== undefined
          ? rowEdits.mappedTaxCategoryId
          : (row?.mappedTaxCategoryId ?? product.mappedTaxCategory?.id ?? null),
    },
  };
}

function readLegacyCompareAtPrice(product: ProductItem, variantId: string): number | null {
  const raw = getRawVariantFromProduct(product, variantId) as { compareAtPrice?: number | null } | null;
  return raw?.compareAtPrice ?? null;
}

export function buildVariantSavePayload(
  row: BulkEditRow,
  rowEdits: BulkEditVariantEdits,
  storeCurrencies: BulkEditStoreCurrencies,
  exchangeRates: Record<string, number>,
): UpdateVariantInput | null {
  const variant = row.sourceVariant;
  if (!variant || Object.keys(rowEdits).length === 0) return null;

  const rawVariant = getRawVariant(row.sourceProduct, variant.id);
  const rawPrices = (rawVariant?.prices ?? variant.prices ?? []) as Array<Record<string, unknown>>;
  if (rawPrices.length === 0) return null;

  let prices = normalizePricesForVariantUpdate(rawPrices);
  const defaultPrice = prices.find((price) => price.default) ?? prices[0];
  if (!defaultPrice?.id) return null;

  const currencyPatches = rowEdits.currencyEdits ?? {};
  const patchedCurrencies = Object.keys(currencyPatches);

  if (patchedCurrencies.length > 0) {
    const baseCurrency = storeCurrencies.baseCurrency.toUpperCase();
    const updatedOptions = { ...(defaultPrice.currencyOptions ?? {}) };

    for (const currency of patchedCurrencies) {
      const upper = currency.toUpperCase();
      updatedOptions[upper] = {
        ...(updatedOptions[upper] ?? { rate: 1, lock: false, date: new Date().toISOString() }),
        amount: currencyPatches[currency]!,
        lock: true,
        lockedBy: 'merchant',
        date: new Date().toISOString(),
      };
    }

    if (patchedCurrencies.some((c) => c.toUpperCase() === baseCurrency)) {
      const baseData = updatedOptions[baseCurrency];
      for (const key of Object.keys(updatedOptions)) {
        if (key !== baseCurrency && !updatedOptions[key]?.lock) {
          const rate = exchangeRates[key.toLowerCase()] ?? 1;
          updatedOptions[key] = {
            ...updatedOptions[key],
            rate,
            amount: convertMinorUnits(baseData.amount, baseCurrency, rate, key),
          };
        }
      }
    }

    prices = prices.map((price) =>
      price.id === defaultPrice.id ? { ...price, currencyOptions: updatedOptions } : price,
    );
  }

  if (
    rowEdits.recurring != null ||
    rowEdits.interval != null ||
    rowEdits.intervalCount != null
  ) {
    prices = prices.map((price) =>
      price.id === defaultPrice.id
        ? {
            ...price,
            recurring: rowEdits.recurring ?? row.recurring ?? price.recurring,
            interval:
              rowEdits.recurring === false
                ? null
                : (rowEdits.interval ?? row.interval ?? price.interval),
            intervalCount:
              rowEdits.recurring === false
                ? null
                : (rowEdits.intervalCount ?? row.intervalCount ?? price.intervalCount),
          }
        : price,
    );
  }

  const active = rowEdits.active !== undefined ? rowEdits.active : row.active;
  const inStock = rowEdits.inStock !== undefined ? rowEdits.inStock : row.inStock;

  return {
    variantId: variant.id,
    updatedData: {
      name: rowEdits.name ?? row.name,
      description: rowEdits.variantDescription ?? row.variantDescription ?? '',
      active,
      inStock,
      default: rowEdits.isDefault !== undefined ? rowEdits.isDefault : row.isDefault,
      sku: rowEdits.sku ?? row.sku ?? null,
      grams: rowEdits.grams ?? row.grams ?? null,
      imageUrl: rowEdits.imageUrl ?? row.imageUrl ?? null,
      price: null,
      compareAtPrice: row.sourceProduct
        ? readLegacyCompareAtPrice(row.sourceProduct, variant.id)
        : null,
      creditPrice: rowEdits.creditPrice ?? row.creditPrice ?? null,
      prices,
    },
  };
}

export function formatPriceDisplay(amount: number | null, currency: string) {
  if (amount == null) return '';
  return minorUnitsToMajorUnits(amount, currency);
}

export function parsePriceInput(value: string, currency: string) {
  return moneyStringOrNumberToMinorUnits(value || '0', currency);
}

export interface BulkEditVariantScalarPatch {
  variantId: string;
  active?: boolean;
  inStock?: boolean;
}

function buildVariantScalarPatch(
  row: BulkEditRow,
  rowEdits: BulkEditVariantEdits,
): BulkEditVariantScalarPatch | null {
  if (!row.sourceVariant) return null;

  const patch: BulkEditVariantScalarPatch = { variantId: row.sourceVariant.id };
  if (rowEdits.active !== undefined) patch.active = rowEdits.active;
  if (rowEdits.inStock !== undefined) patch.inStock = rowEdits.inStock;

  return patch.active !== undefined || patch.inStock !== undefined ? patch : null;
}

function mergeVariantScalarPatch(
  current: BulkEditVariantScalarPatch | undefined,
  patch: BulkEditVariantScalarPatch,
): BulkEditVariantScalarPatch {
  return {
    variantId: patch.variantId,
    active: patch.active ?? current?.active,
    inStock: patch.inStock ?? current?.inStock,
  };
}

export function collectSavePayloads(
  products: ProductItem[],
  rows: BulkEditRow[],
  edits: Map<string, BulkEditRowEdits>,
  storeCurrencies: BulkEditStoreCurrencies,
  exchangeRates: Record<string, number>,
): {
  productUpdates: UpdateProductInput[];
  variantUpdates: UpdateVariantInput[];
  variantScalarPatches: BulkEditVariantScalarPatch[];
} {
  const productEditsById = new Map<
    string,
    { product: ProductItem; edits: BulkEditProductEdits; row?: BulkEditRow }
  >();
  const variantEditsById = new Map<string, { row: BulkEditRow; edits: BulkEditVariantEdits }>();

  for (const [key, rowEdits] of edits) {
    const { kind, id } = parseRowKey(key);
    const row = rows.find((entry) => entry.key === key);

    if (kind === 'product') {
      const product = products.find((entry) => entry.id === id);
      if (!product) continue;

      const variants = product.variants ?? [];
      const syncedProductEdits = productEditsFromSingleVariantProductRow(rowEdits, variants.length);
      const productOnlyEdits = syncedProductEdits ?? pickProductEdits(rowEdits);
      if (Object.keys(productOnlyEdits).length > 0) {
        enqueueProductUpdate(productEditsById, product, productOnlyEdits, row);
      }

      if (variants.length <= 1 && row?.sourceVariant) {
        const variantEdits = mergeVariantEditsForSingleVariantProduct(rowEdits, variants.length);
        if (Object.keys(variantEdits).length > 0) {
          enqueueVariantUpdate(variantEditsById, row, variantEdits);
        }
      }

      continue;
    }

    if (!row?.sourceVariant) continue;

    const variantEdits = pickVariantEdits(rowEdits);
    enqueueVariantUpdate(variantEditsById, row, variantEdits);

    const product = products.find((entry) => entry.id === row.productId);
    const mirroredProductEdits = productEditsFromSingleVariantRow(row, variantEdits);
    if (product && mirroredProductEdits) {
      enqueueProductUpdate(productEditsById, product, mirroredProductEdits, row);
    }
  }

  const productUpdates: UpdateProductInput[] = [];
  for (const { product, edits: productEdits, row } of productEditsById.values()) {
    const payload = buildProductSavePayload(product, productEdits, row);
    if (payload) productUpdates.push(payload);
  }

  const variantUpdates: UpdateVariantInput[] = [];
  const variantScalarPatchesById = new Map<string, BulkEditVariantScalarPatch>();

  for (const { row, edits: variantEdits } of variantEditsById.values()) {
    const payload = buildVariantSavePayload(row, variantEdits, storeCurrencies, exchangeRates);
    if (payload) {
      variantUpdates.push(payload);
      continue;
    }

    const scalarPatch = buildVariantScalarPatch(row, variantEdits);
    if (scalarPatch) {
      variantScalarPatchesById.set(
        scalarPatch.variantId,
        mergeVariantScalarPatch(variantScalarPatchesById.get(scalarPatch.variantId), scalarPatch),
      );
    }
  }

  return {
    productUpdates,
    variantUpdates,
    variantScalarPatches: [...variantScalarPatchesById.values()],
  };
}

export function columnLabelKey(column: BulkEditColumnId) {
  return `products:bulkEditor.columns.${column}`;
}

export function getBulkEditColumnLabel(t: TFunction, column: BulkEditColumnId) {
  if (column === 'price') {
    return t('products:bulkEditor.columns.prices');
  }
  return t(columnLabelKey(column));
}

export function buildDefaultVisibleColumns(): Set<BulkEditColumnId> {
  return new Set(DEFAULT_VISIBLE_STATIC_COLUMNS);
}

/** Columns available in the customize-columns menu (excludes internal/legacy fields). */
export function getCustomizeColumnOptions(isCreditEnabled: boolean): BulkEditColumnId[] {
  return ALL_BULK_EDIT_STATIC_COLUMN_IDS.filter(
    (column) => column !== 'creditPrice' || isCreditEnabled,
  );
}

export function isVariantFieldDisabled(isProduct: boolean, hasChildVariants: boolean) {
  return isProduct && hasChildVariants;
}
